import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import React from 'react';

// Generate random widths for more realistic appearance
const getSkeletonWidth = ({
  columnIndex,
  isHeader = false,
  columns,
  columnWidths,
  rowIndex = 0,
}: {
  columnIndex: number;
  isHeader?: boolean;
  columns: number;
  columnWidths?: string[];
  rowIndex?: number;
}) => {
  // Guard against division by zero
  if (columns === 0) {
    return 'w-24'; // Default width
  }

  if (columnWidths && columnWidths[columnIndex]) {
    return columnWidths[columnIndex];
  }

  // Predefined widths for more realistic appearance
  const widths = isHeader
    ? ['w-24', 'w-32', 'w-28', 'w-20', 'w-36', 'w-24', 'w-40']
    : [
        ['w-20', 'w-32', 'w-24', 'w-16', 'w-28', 'w-20', 'w-36'],
        ['w-24', 'w-28', 'w-32', 'w-20', 'w-24', 'w-32', 'w-28'],
        ['w-32', 'w-20', 'w-28', 'w-24', 'w-32', 'w-28', 'w-24'],
        ['w-16', 'w-36', 'w-20', 'w-32', 'w-20', 'w-24', 'w-32'],
        ['w-28', 'w-24', 'w-32', 'w-28', 'w-36', 'w-20', 'w-28'],
      ];

  if (isHeader) {
    return widths[columnIndex % widths.length];
  }

  const effectiveRowIndex = rowIndex % widths.length;
  return widths[effectiveRowIndex][
    columnIndex % widths[effectiveRowIndex].length
  ];
};

interface TableSkeletonProps {
  /**
   * Number of rows to display in the skeleton
   * @default 5
   */
  rows?: number;
  /**
   * Number of columns to display in the skeleton
   * @default 4
   */
  columns?: number;
  /**
   * Whether to show the table header skeleton
   * @default true
   */
  showHeader?: boolean;
  /**
   * Custom column widths for more realistic skeletons
   * If not provided, will use random widths
   */
  columnWidths?: string[];
  /**
   * Additional CSS classes for the table container
   */
  className?: string;
}

function TableSkeletonBase({
  rows = 5,
  columns = 4,
  showHeader = true,
  columnWidths,
  className,
}: TableSkeletonProps) {
  return (
    <div className={className}>
      <Table>
        {showHeader && (
          <TableHeader>
            <TableRow>
              {Array.from({ length: columns }).map((_, index) => (
                <TableHead key={`header-${index}`}>
                  <Skeleton
                    className={`h-4 ${getSkeletonWidth({
                      columnIndex: index,
                      isHeader: true,
                      columns,
                      columnWidths,
                    })}`}
                  />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        )}
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow key={`row-${rowIndex}`}>
              {Array.from({ length: columns }).map((_, columnIndex) => (
                <TableCell key={`cell-${rowIndex}-${columnIndex}`}>
                  <Skeleton
                    className={`h-4 ${getSkeletonWidth({
                      columnIndex,
                      isHeader: false,
                      columns,
                      columnWidths,
                      rowIndex,
                    })}`}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export const TableSkeleton = React.memo(TableSkeletonBase);
